import React, { useState } from "react";
import {
    Grid,
    TextField,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Box,
    Typography,
    Paper,
    Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";

// Password validation regex: min 8 chars, 1 uppercase, 1 special character
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

export default function ChangePassword() {
    const navigate = useNavigate();
    const jwt = localStorage.getItem("jwt");

    // Form fields
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");

    // UI states
    const [isLoading, setIsLoading] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const handleCloseSnackbar = () => {
        setOpenSnackBar(false);
    };

    const validateNewPassword = (pwd) => {
        if (!PASSWORD_REGEX.test(pwd)) {
            setPasswordError("Min 8 characters, 1 uppercase, 1 special character");
            return false;
        }
        setPasswordError("");
        return true;
    };

    const validateConfirmPassword = (confirmPwd) => {
        if (confirmPwd !== newPassword) {
            setConfirmPasswordError("Passwords do not match");
            return false;
        }
        setConfirmPasswordError("");
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Validate all fields
        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setSnackbarMessage("Please fill in all fields");
            setSnackbarSeverity("error");
            setOpenSnackBar(true);
            return;
        }

        if (!validateNewPassword(newPassword)) {
            setSnackbarMessage("Please fix the password errors");
            setSnackbarSeverity("error");
            setOpenSnackBar(true);
            return;
        }

        if (!validateConfirmPassword(confirmNewPassword)) {
            setSnackbarMessage("Passwords do not match");
            setSnackbarSeverity("error");
            setOpenSnackBar(true);
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/users/change-password`,
                {
                    currentPassword,
                    newPassword,
                    confirmNewPassword,
                },
                {
                    headers: {
                        Authorization: `Bearer ${jwt}`,
                    },
                }
            );

            setSnackbarMessage("Password changed successfully!");
            setSnackbarSeverity("success");
            setOpenSnackBar(true);

            // Clear form
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");

            // Password changed successfully - redirect to home
            setTimeout(() => {
                navigate("/");
            }, 2000);
        } catch (error) {
            const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Failed to change password. Please try again.";
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setOpenSnackBar(true);
        } finally {
            setIsLoading(false);
        }
    };

    // Redirect if not logged in
    if (!jwt) {
        navigate("/login");
        return null;
    }

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: "#9155FD",
                            borderRadius: "50%",
                            p: 1.5,
                            mb: 2,
                        }}
                    >
                        <LockOutlinedIcon sx={{ color: "white", fontSize: 32 }} />
                    </Box>

                    <Typography variant="h5" component="h1" gutterBottom>
                        Change Password
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, textAlign: "center" }}
                    >
                        Enter your current password and choose a new secure password.
                    </Typography>

                    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    id="currentPassword"
                                    name="currentPassword"
                                    label="Current Password"
                                    fullWidth
                                    type="password"
                                    autoComplete="current-password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    required
                                    id="newPassword"
                                    name="newPassword"
                                    label="New Password"
                                    fullWidth
                                    type="password"
                                    autoComplete="new-password"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        if (e.target.value) validateNewPassword(e.target.value);
                                        if (confirmNewPassword)
                                            validateConfirmPassword(confirmNewPassword);
                                    }}
                                    error={!!passwordError}
                                    helperText={
                                        passwordError ||
                                        "Min 8 characters, 1 uppercase, 1 special character"
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    required
                                    id="confirmNewPassword"
                                    name="confirmNewPassword"
                                    label="Confirm New Password"
                                    fullWidth
                                    type="password"
                                    autoComplete="new-password"
                                    value={confirmNewPassword}
                                    onChange={(e) => {
                                        setConfirmNewPassword(e.target.value);
                                        if (e.target.value) validateConfirmPassword(e.target.value);
                                    }}
                                    error={!!confirmPasswordError}
                                    helperText={confirmPasswordError}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    sx={{
                                        padding: ".8rem 0",
                                        backgroundColor: "#9155FD",
                                        "&:hover": { backgroundColor: "#7c3fe6" },
                                    }}
                                    disabled={
                                        isLoading || !!passwordError || !!confirmPasswordError
                                    }
                                >
                                    {isLoading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        "Change Password"
                                    )}
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    variant="text"
                                    fullWidth
                                    onClick={() => navigate(-1)}
                                    sx={{ color: "#9155FD" }}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Paper>

            <Snackbar
                open={openSnackBar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}
